<template>
  <left-box-element
    @update:size="setOptimalChartSize"
  >
    <template #element-title>
      <h1>{{ $t("helpdesk.landingPage.processingSectionTitle") }}</h1>
    </template>

    <template #element-content>
      <el-container
        v-if="!tableData.length"
        v-loading="isLoading"
        class="landing-page-element-body loading-box"
      />

      <transition name="el-fade-in-linear">
        <el-container
          v-if="tableData.length"
          v-loading="isLoading"
          class="landing-page-element-body"
        >
          <el-aside class="table-box">
            <el-table
              :data="tableData"
              stripe
              border
              size="small"
              class="processing-of-requests-table"
              :header-cell-class-name="processingTableHeaderClasses"
            >
              <el-table-column
                v-for="(column, index) in processingOfRequestsTableColumns"
                :key="index"
                :prop="column"
                :label="column"
                :class-name="column === 'total' ? 'total-column' : ''"
                align="center"
              />
            </el-table>
          </el-aside>

          <el-main class="chart-box">
            <pie-chart
              :pie-series="pieSeries"
              :pie-chart-size="optimalPieSize"
            />
          </el-main>
        </el-container>
      </transition>
    </template>
  </left-box-element>
</template>

<script lang="ts">
import { Vue, Component, Watch } from 'vue-property-decorator'

import LeftBoxElement from '@/components/Helpdesk/LandingPage/LeftBoxElement.vue'

import PieChart from '@/components/UI/PieChart.vue'

import {
  TicketsStatisticsQuery
} from '@/types/graphql'

interface ProcessingOfRequestsTable {
  open: string
  pending: string
  closed: string
  total: string
}

@Component({
  components: {
    LeftBoxElement,
    PieChart
  }
})

export default class extends Vue {
  private tableData: Array<ProcessingOfRequestsTable> = []

  private processingOfRequestsTableColumns: Array<string> = []

  private pieSeries: Array<number> = []

  private isLoading = false

  private optimalPieSize = 0

  private keyToLocalizedLabel(key: string) {
    let label = key.replace('_tickets', '')
    if (this.$te('helpdesk.landingPage.tableLabel_' + label)) {
      label = this.$t('helpdesk.landingPage.tableLabel_' + label).toString()
    }
    return label
  }

  private processingOfRequestsTableData(statusCount: TicketsStatisticsQuery['ticketsStatistics']['status_counts']) {
    const countRow = {}
    const percentageRow = {}

    Object.keys(statusCount).forEach(key => {
      const statusCountKey = key as keyof TicketsStatisticsQuery['ticketsStatistics']['status_counts']
      if ((statusCount[statusCountKey] as number) > 0) {
        const newKey = this.keyToLocalizedLabel(statusCountKey as string)
        Vue.set(countRow, newKey, statusCount[statusCountKey]?.toString())
        Vue.set(percentageRow, newKey, Math.round((statusCount[statusCountKey] as number) * 100 / statusCount.total_tickets) + '%')
      }
    })

    this.processingOfRequestsTableColumns = Object.keys(countRow).map(key => key)

    this.pieSeries = (Object.values(countRow).map(values => { return parseInt(values as string) }) as Array<number>).slice(0, -1)

    this.tableData.push(countRow as ProcessingOfRequestsTable, percentageRow as ProcessingOfRequestsTable)
  }

  private processingTableHeaderClasses(row: any) {
    return ('headerCell' + row.columnIndex).toString()
  }

  private setOptimalChartSize(val: any) {
    this.optimalPieSize = Math.min(val.elementHeight, Math.round(val.elementWidth / 4)) - 20
  }

  private async getTicketStatisticsQuery() {
    this.isLoading = true
    this.$apollo
      .query({
        query: TicketsStatisticsQuery,
        fetchPolicy: 'network-only'
      })
      .then(res => {
        this.tableData = []
        this.processingOfRequestsTableData(res.data.ticketsStatistics.status_counts)
      })
      .finally(() => {
        this.isLoading = false
      })
  }

  created() {
    this.getTicketStatisticsQuery()
  }

  @Watch('$i18n.locale')
  private languageChanged() {
    this.getTicketStatisticsQuery()
  }
}
</script>

<style lang="scss">
  .processing-of-requests-table {
    .headerCell0 {
      background-image: linear-gradient(hsl(0, 100%, 50%), hsl(0, 0%, 97%) 0.1) !important;
    }
    .headerCell1 {
      background-image: linear-gradient(hsl(47, 100%, 48%), hsl(0, 0%, 97%) 0.1) !important;
    }
    .headerCell2 {
      background-image: linear-gradient(hsl(161, 67%, 47%), hsl(0, 0%, 97%) 0.1) !important;
    }
  }
</style>
